import { DragDropContext, Draggable, DropResult, Droppable } from "@hello-pangea/dnd";
import React from "react";
import { Col, Form, Row } from "react-bootstrap";

import DstButton from "../../../../../components/DstButton/DstButton";
import MultiSelectLiveDropdown from "../../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import { formatDateHumanly, formatToISODateTime } from "../../../../../services/TimeService";
import translate from "../../../../../services/Translate";
import { sprintObject } from "../CohortSprint.d";

const _handleOptionalChange = (sprintIndex: number, lessonIndex: number, setSprints: Function) => {
  setSprints((prevArray: sprintObject[]) => {
    const newStateArray = [...prevArray];
    newStateArray[sprintIndex].modules[lessonIndex].is_mandatory =
      !newStateArray[sprintIndex].modules[lessonIndex].is_mandatory;
    return newStateArray;
  });
};

const _handleSprintChange = (
  index: number,
  key: "name" | "dt_start" | "dt_end",
  newValue: string,
  setSprints: Function
) => {
  setSprints((prevArray: sprintObject[]) => {
    const newStateArray = [...prevArray];
    newStateArray[index].sprint[key] = newValue;
    return newStateArray;
  });
};

const _handleModuleAdd = (selectedItems: any, index: number, setSprints: Function) => {
  setSprints((prevArray: sprintObject[]) => {
    const newStateArray = [...prevArray];
    newStateArray[index].modules = [
      ...newStateArray[index].modules,
      ...selectedItems.map((item: any) => ({
        module: item.value.id,
        name: item.value.name,
        is_mandatory: true,
      })),
    ];
    return newStateArray;
  });
};

const _handleModuleDelete = (sprintIndex: number, moduleIndex: number, setSprints: Function) => {
  setSprints((prevArray: any[]) => {
    const newStateArray = [...prevArray];
    newStateArray[sprintIndex].modules = newStateArray[sprintIndex].modules.filter(
      (_: any, index: number) => index !== moduleIndex
    );
    return newStateArray;
  });
};

const _handleDragEnd = (result: DropResult, sprintIndex: number, setSprints: Function) => {
  if (!result.destination) return;

  setSprints((prevArray: sprintObject[]) => {
    const newStateArray = [...prevArray];
    const [movedLesson] = newStateArray[sprintIndex].modules.splice(result.source.index, 1);
    newStateArray[sprintIndex].modules.splice(result.destination!.index, 0, movedLesson);
    return newStateArray;
  });
};

export const buildPopupTabs = (
  language: string,
  sprints: sprintObject[],
  setSprints: Function,
  handleDeleteSprint: Function
) => {
  return sprints.map((sprintData: sprintObject, index: number) => {
    const startDate = sprintData.sprint.dt_start ? formatToISODateTime(sprintData.sprint.dt_start, true) : "";
    const endDate = sprintData.sprint.dt_end ? formatToISODateTime(sprintData.sprint.dt_end, true) : "";

    return {
      id: `sprint-${index}`,
      key: `tab-${index}-${sprintData.sprint.id}`,
      title: (
        <React.Fragment>
          <div className="s-bold">{sprintData.sprint.name}</div>
          <div className="xxs-regular smoke-gray-font">
            {formatDateHumanly(language, startDate, true)} - {formatDateHumanly(language, endDate, true)}
          </div>
        </React.Fragment>
      ),
      content: (
        <div className="white-background m-3 p-3">
          <div className="d-flex justify-content-end">
            <DstButton
              variant="outline-danger"
              btnClass="d-flex align-items-center border-0"
              btnImageBefore={<span className="dst-icon-trash-01 d-flex m-regular me-2" />}
              clickFunction={() => handleDeleteSprint(index)}
              value={translate(language, "PAGES.COHORT.SPRINT.POPUP.INFO_SPRINT.DELETE_SPRINT")}
            />
          </div>
          <Form.Group controlId={`name-${index}`} className="mb-3 w-100">
            <Form.Label>{translate(language, "GLOBAL.NAME")}</Form.Label>
            <Form.Control
              type="text"
              value={sprintData.sprint.name}
              onChange={(e) => _handleSprintChange(index, "name", e.target.value, setSprints)}
              aria-label="Name"
            />
          </Form.Group>
          <Row>
            <Col>
              <Form.Group controlId={`startDate-${index}`} className="mb-3">
                <Form.Label>{translate(language, "GLOBAL.START_DATE")}</Form.Label>
                <Form.Control
                  type="datetime-local"
                  defaultValue={startDate}
                  onBlur={(e) => _handleSprintChange(index, "dt_start", e.target.value, setSprints)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId={`endDate-${index}`} className="mb-3">
                <Form.Label>{translate(language, "GLOBAL.END_DATE")}</Form.Label>
                <Form.Control
                  type="datetime-local"
                  defaultValue={endDate}
                  onBlur={(e) => _handleSprintChange(index, "dt_end", e.target.value, setSprints)}
                />
              </Form.Group>
            </Col>
          </Row>
          <DragDropContext onDragEnd={(result) => _handleDragEnd(result, index, setSprints)}>
            <Droppable droppableId={`droppable-${index}`}>
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {sprintData.modules.map((module: any, moduleIndex: number) => (
                    <Draggable
                      key={module.module.toString()}
                      draggableId={module.module.toString()}
                      index={moduleIndex}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="mb-3 draggable-row"
                        >
                          <div className="border-bottom d-flex justify-content-between py-2">
                            <div className="d-flex">
                              <span className="dst-icon-align-justify me-3 align-content-center" />
                              <div className="d-flex align-items-center">
                                <div className="badge bg-primary me-2 min-3ch">{module.module}</div>
                              </div>
                              <div className="me-3">{module.name}</div>
                            </div>
                            <div className="text-nowrap align-content-center">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={!module.is_mandatory}
                                  onChange={() => _handleOptionalChange(index, moduleIndex, setSprints)}
                                />
                                <span className="ms-2">
                                  {translate(language, "PAGES.COHORT.SPRINT.POPUP.INFO_SPRINT.OPTIONAL")}
                                </span>
                              </label>
                              <span
                                className="dst-icon-trash-03 ms-3"
                                onClick={() => _handleModuleDelete(index, moduleIndex, setSprints)}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <div className="border-bottom">
            <MultiSelectLiveDropdown
              label={
                <React.Fragment>
                  <span className="dst-icon-package-plus me-3 align-content-center blue-font" />
                  <span className="blue-font">
                    {translate(language, "PAGES.COHORT.SPRINT.POPUP.INFO_SPRINT.ADD_MODULE")}
                  </span>
                </React.Fragment>
              }
              category="modules"
              language={language}
              hideCaret={true}
              onConfirm={(selectedItems) => _handleModuleAdd(selectedItems, index, setSprints)}
              clearOnConfirm={true}
              toggleClass="btn-secondary mb-1"
              filterCallback={(module: any) => !module.is_deprecated}
            />
          </div>
        </div>
      ),
    };
  });
};
